import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';

import { TranslateService }                             from '@ngx-translate/core';

import { HaynesProMaintenanceExtraDataSectionModel } from './typings/haynes-pro.maintenance-extra-data-section';
import { HaynesProServiceItemIntervalModel }         from './typings/haynes-pro.service-item-interval';
import { PushPopComponent }                          from '../table/cell-components/actions/push-pop.component';
import { Table }                                     from '../table/types/table';

import {
  HaynesProTimingBeltSystemModel,
  HaynesProTimingBeltTaskModel
}  from './typings/haynes-pro.timing-belt';

@Component({
  template: require('./haynes-pro.maintenance-times.extra-data.row.expanded.component.html')
})
export class HaynesProMaintenanceTimesExtraDataExpandedRowComponent implements OnChanges, OnInit {
  public selectedTimingBeltSystemName: string | null = null;
  public serviceIntervalSettings     : Table;
  public timingBeltTaskSettings      : Table;

  @Input() record          : HaynesProMaintenanceExtraDataSectionModel;
  @Input() rowExpansionData: ( row : HaynesProMaintenanceExtraDataSectionModel ) => void;
  @Input() viewMode        : boolean;

  @Output() onTimeAdd   : EventEmitter<any> = new EventEmitter();
  @Output() onTimeRemove: EventEmitter<any> = new EventEmitter()

  constructor (
    public translateService: TranslateService
  ) {}

  ngOnChanges ( changes : SimpleChanges ) : void {
    if (changes.record && this.isTimingBeltTemplate) {
      this.enrichTimingBeltTasks();
      this.setDefaultTimingBeltSystem();
    }
  }

  ngOnInit () : void {
    if (typeof this.rowExpansionData === 'function') {
      this.rowExpansionData(this.record);
    }

    this.serviceIntervalSettings = {
      columns: [
        {
          field : 'name',
          header: {
            text: this.translateService.instant('GENERAL_SPACE.FIELD.NAME')
          }
        },
        {
          field : 'interval',
          header: {
            text: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.PERIOD')
          }
        }
      ],
      pagination: {
        enabled: false
      },
      sorting: {
        enabled: false
      },
      static: true
    };

    this.timingBeltTaskSettings = {
      columns: [
        {
          field : 'name',
          header: {
            text: this.translateService.instant('GENERAL_SPACE.FIELD.NAME')
          }
        },
        {
          field : 'remark',
          header: {
            text: this.translateService.instant('GENERAL_SPACE.FIELD.REMARK')
          }
        },
        {
          classes : [ 'text-center', 'color-red' ],
          field   : '__mandatoryTick',
          header  : {
            classes: 'text-center',
            text   : this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.MANDATORY_REPLACEMENT')
          },
          sortable: false
        },
        {
          field : '__labourTimeHours',
          header: {
            text: this.translateService.instant('GENERAL_SPACE.FIELD.LABOUR_TIME')
          }
        }
      ],
      pagination: {
        enabled: false
      },
      sorting: {
        enabled: false
      },
      static: true
    };

    if (!this.viewMode) {
      this.timingBeltTaskSettings.columns.push({
        classes  : [ 'text-center' ],
        component: {
          bindings: {
            inputs: {
              popTitle : this.translateService.instant('GENERAL_SPACE.BUTTON.REMOVE_FROM_CART'),
              pushTitle: this.translateService.instant('GENERAL_SPACE.BUTTON.ADD_TO_CART'),
            },
            outputs: {
              onPop : ( task : HaynesProTimingBeltTaskModel ) => this.handleTimingBeltTaskRemoval(task),
              onPush: ( task : HaynesProTimingBeltTaskModel ) => this.handleTimingBeltTaskSelection(task)
            }
          },
          enabled: true,
          factory: PushPopComponent,
        },
        sortable: false,
      });
    }

    this.enrichTimingBeltTasks();
    this.setDefaultTimingBeltSystem();
  }

  private enrichTimingBeltTasks () : void {
    if (!this.isTimingBeltTemplate) {
      return;
    }

    this.timingBeltSystems.forEach(( system : HaynesProTimingBeltSystemModel ) => {
      (system?.tasks || []).forEach(( task : HaynesProTimingBeltTaskModel ) => {
        const rawValue = task?.times?.[0]?.value;

        task.__labourTimeHours = typeof rawValue === 'number' ? rawValue / 100 : null;
        task.__mandatoryTick   = task?.mandatoryReplacement ? '✔' : '';
      });
    });
  }

  get isServiceIntervalsTemplate () : boolean {
    return this.record?.type === 'serviceIntervals';
  }

  get isTimingBeltTemplate () : boolean {
    return this.record?.type === 'timingBelt';
  }

  get serviceIntervals () : Array<HaynesProServiceItemIntervalModel> {
    return Array.isArray(this.record?.items) ? this.record.items : [];
  }

  get selectedTimingBeltTasks () : Array<HaynesProTimingBeltTaskModel> {
    const selectedSystem = this.timingBeltSystems.find(( system : HaynesProTimingBeltSystemModel ) =>
      system?.system?.name === this.selectedTimingBeltSystemName
    );

    return selectedSystem?.tasks || [];
  }

  get timingBeltSystems () : Array<HaynesProTimingBeltSystemModel> {
    return Array.isArray(this.record?.items) ? this.record.items : [];
  }

  handleTimingBeltSystemSelection ( system : HaynesProTimingBeltSystemModel ) : void {
    this.selectedTimingBeltSystemName = system?.system?.name || null;
  }

  handleTimingBeltTaskSelection ( task : HaynesProTimingBeltTaskModel ) : void {
    task.added = true;
    this.onTimeAdd.emit(task);
  }

  handleTimingBeltTaskRemoval ( task : HaynesProTimingBeltTaskModel ) : void {
    task.added = false;
    this.onTimeRemove.emit(task);
  }

  isSelectedTimingBeltSystem ( system : HaynesProTimingBeltSystemModel ) : boolean {
    return system?.system?.name === this.selectedTimingBeltSystemName;
  }

  private setDefaultTimingBeltSystem () : void {
    if (!this.isTimingBeltTemplate) {
      return;
    }

    if (!this.selectedTimingBeltSystemName && this.timingBeltSystems.length > 0) {
      this.selectedTimingBeltSystemName = this.timingBeltSystems[0].system?.name || null;
    }
  }
}